import type { DateRange } from "@cubejs-client/core";
import { escapeContextAttr, escapeContextText } from "./shared/prompt-context";
import { simpleAspectRatio } from "./shared/creative-dimensions";
import type {
  SavedStyle,
  SuggestedAction,
  SuggestedActionId,
} from "./ad-suggested-actions";
import type { AdMedium } from "./ad-medium";
import type {
  EntityStatus,
  Found,
  LoadedSize,
  Reported,
  SizedMedia,
} from "./performance/types";

type CreativeCopy = {
  primaryText: Found<string>;
  headline: Found<string>;
  description: Found<string>;
  callToAction: Found<string>;
  destinationUrl: Found<string>;
};

type PromptFatigue = {
  status: string;
  statusLabel: string;
  peakCtr: Found<string>;
  vsPeakPct: Reported;
  wowCtrChange: Reported;
  cause: Found<string>;
  daysActive: number;
};

export type AdFacts = {
  entityId: string;
  entityName: string;
  campaignName: Found<string>;
  adSetName: Found<string>;
  status: EntityStatus;
  creativeCopy: CreativeCopy;
  tags: readonly string[];
  metrics: readonly { label: string; value: string }[];
  fatigue: PromptFatigue | "no-fatigue-row";
};

/** The creative the action acts on, as it was on screen when picked. */
export type PromptCreative = {
  media: SizedMedia<LoadedSize> | { kind: "no-media" };
};

export type AdForPrompt = AdFacts & { creative: PromptCreative };

/** How the size sentence refers to what it is asking for, and to the ad it
 * came from — a refresh replaces its ad, a cut-down does not. */
type SizeSentenceSubject = { noun: string; source: string };

type PromptSpec = {
  text: string;
  /** Null for actions whose whole point is to change the size. */
  size: SizeSentenceSubject | null;
};

/**
 * Editable text the composer opens with. "Hold the message" is keyed on the
 * fields that carry one, not on `creativeCopy`: telling the agent to hold copy
 * it cannot see has it inventing an angle from the ad's name. The size
 * constraint lives here, not in `<ad-context>`, so it stays editable.
 */
export function suggestedActionPrompt({
  action,
  ad,
}: {
  action: SuggestedAction;
  ad: AdForPrompt;
}): string {
  const spec =
    action.id === "remix_in_style"
      ? remixSpec(ad, action.style)
      : PROMPT_SPECS[action.id](ad);
  const size = spec.size ? sizeSentence(ad.creative, spec.size) : null;
  return [spec.text, size].filter(isPresent).join(" ");
}

const CONCEPT_FROM_AD: SizeSentenceSubject = {
  noun: "concept",
  source: "comes from",
};

/** Without this the agent matches the aspect ratio and builds stills: the
 * medium reaches it as display metadata, and nothing else asks for motion. */
const VIDEO_WORKFLOW =
  "The source ad is a video, so build motion with the ad-video skill rather than stills.";

function remixSpec(ad: AdForPrompt, style: SavedStyle): PromptSpec {
  return {
    text: [
      `Remix the ad in the report below in our "${style.name}" style.`,
      hasMessage(ad)
        ? "Hold the messaging angle, and rebuild the visual direction to match the style."
        : "Rebuild the visual direction to match the style.",
      "Generate 4 concepts.",
    ].join(" "),
    size: CONCEPT_FROM_AD,
  };
}

const PROMPT_SPECS: Record<
  Exclude<SuggestedActionId, "remix_in_style">,
  (ad: AdForPrompt) => PromptSpec
> = {
  riff: (ad) =>
    isVideo(ad)
      ? {
          text: [
            "Riff on the ad in the report below and generate 5 new video concepts from its strongest angle.",
            hasMessage(ad)
              ? "Hold the messaging angle; vary the hook, visual direction, and pacing."
              : "Vary the hook, visual direction, and pacing.",
            VIDEO_WORKFLOW,
          ].join(" "),
          size: CONCEPT_FROM_AD,
        }
      : {
          text: hasMessage(ad)
            ? "Riff on the ad in the report below and generate 5 new concepts from its strongest angle. Hold the messaging angle; vary the hook, visual direction, and format."
            : "Riff on the ad in the report below and generate 5 new concepts from its strongest angle. Vary the hook, visual direction, and format.",
          size: CONCEPT_FROM_AD,
        },

  refresh: (ad) =>
    isVideo(ad)
      ? {
          text: [
            hasMessage(ad)
              ? "Read the fatigue report below and generate 5 fresh video variations of this ad. Hold the message and offer from its copy; vary the hook, visual direction, and pacing."
              : "Read the fatigue report below and generate 5 fresh video variations for this ad's campaign and audience. Vary the hook, visual direction, and pacing.",
            VIDEO_WORKFLOW,
          ].join(" "),
          size: { noun: "video", source: "replaces" },
        }
      : {
          text: hasMessage(ad)
            ? "Read the fatigue report below and generate 5 fresh variations of this ad. Hold the message and offer from its copy; vary the hook, visual direction, and format."
            : "Read the fatigue report below and generate 5 fresh variations for this ad's campaign and audience. Vary the hook, visual direction, and format.",
          size: { noun: "primary", source: "replaces" },
        },

  convert_to_motion: (ad) => ({
    text: hasMessage(ad)
      ? "Convert the ad in the report below into motion. Hold its message, offer, and visual identity, then generate 3 short video concepts. Make them dynamic and interesting with masks and movement. Create scenes with depth, not slideshow-like graphics."
      : "Convert the ad in the report below into motion. Hold its visual identity, then generate 3 short video concepts. Make them dynamic and interesting with masks and movement. Create scenes with depth, not slideshow-like graphics.",
    size: { noun: "video", source: "comes from" },
  }),

  resize: (ad) => ({
    text: hasMessage(ad)
      ? "Resize the ad in the report below for the placements we're missing. Hold its message, offer, and layout intent, and rebuild it at 1:1, 4:5, and 9:16. Skip the ratio it is already built at."
      : "Resize the ad in the report below for the placements we're missing. Hold its layout intent and rebuild it at 1:1, 4:5, and 9:16. Skip the ratio it is already built at.",
    size: null,
  }),

  cut_down: (ad) => ({
    text: [
      "Cut the ad in the report below down into social-first edits. Generate 3 cuts: a 6-second hook-forward version, a 15-second version, and one remix that reorders the scenes around a different hook.",
      hasMessage(ad) ? "Hold the message and offer." : null,
    ]
      .filter(isPresent)
      .join(" "),
    size: { noun: "cut", source: "comes from" },
  }),

  create_statics: (ad) => ({
    text: hasMessage(ad)
      ? "Use the video ad in the report below as the inspiration for 5 new static variants. Pull its strongest moments and visual identity into stills, hold its message and offer, and vary the hook and layout across the set."
      : "Use the video ad in the report below as the inspiration for 5 new static variants. Pull its strongest moments and visual identity into stills, and vary the hook and layout across the set.",
    size: { noun: "static", source: "comes from" },
  }),
};

function hasMessage(ad: AdForPrompt): boolean {
  const { primaryText, headline } = ad.creativeCopy;
  return primaryText.kind === "found" || headline.kind === "found";
}

export function adMediumOf(creative: PromptCreative): AdMedium {
  return creative.media.kind === "video" ? "video" : "static";
}

function isVideo(ad: AdForPrompt): boolean {
  return adMediumOf(ad.creative) === "video";
}

/**
 * Names the ratio, not the measured pixels: the agent maps a ratio onto its own
 * 2K canvas instead of rebuilding at Meta's downsized copy.
 */
function sizeSentence(
  creative: PromptCreative,
  { noun, source }: SizeSentenceSubject,
): string | null {
  const size = fileSizeOf(creative.media);
  return size === "no-file" ? null : fileSizeSentence({ size, noun, source });
}

/** The size of the file the creative shows or plays; YouTube and no media have none. */
function fileSizeOf(media: PromptCreative["media"]): LoadedSize | "no-file" {
  switch (media.kind) {
    case "no-media": {
      return "no-file";
    }
    case "image": {
      return media.size;
    }
    case "video": {
      switch (media.video.kind) {
        case "file": {
          return media.video.size;
        }
        case "youtube": {
          return "no-file";
        }
        default: {
          return media.video satisfies never;
        }
      }
    }
    default: {
      return media satisfies never;
    }
  }
}

function fileSizeSentence({
  size,
  noun,
  source,
}: SizeSentenceSubject & { size: LoadedSize }): string | null {
  switch (size.kind) {
    case "measured": {
      const ratio = simpleAspectRatio(size);
      return ratio.kind === "simple"
        ? `Build each ${noun} at ${ratio.label} to match the ad it ${source}.`
        : `Build each ${noun} at the same aspect ratio as the ad it ${source}.`;
    }
    case "load-failed": {
      return null;
    }
    default: {
      return size satisfies never;
    }
  }
}

const dateRangeText = (dateRange: DateRange): string =>
  typeof dateRange === "string"
    ? dateRange
    : `${dateRange[0]} to ${dateRange[1]}`;

/**
 * The `<ad-context>` block composed into the initial prompt sent to the
 * ad-generation agent. Every value in it is untrusted — see `line`.
 */
export function formatAdForPrompt({
  ad,
  dateRange,
}: {
  ad: AdForPrompt;
  dateRange: DateRange;
}): string {
  const attrs = [
    `ad-id="${escapeContextAttr(ad.entityId)}"`,
    `ad-name="${escapeContextAttr(ad.entityName)}"`,
    ...fatigueAttrs(ad.fatigue),
  ].join(" ");

  const sections = [
    ...section("Ad", [
      ...line("Name", ad.entityName),
      ...foundLine("Campaign", ad.campaignName),
      ...foundLine("Ad set", ad.adSetName),
      ...statusLines(ad.status),
      ...formatLines(ad.creative.media),
      ...sizeLines(ad.creative),
      ...foundLine("Creative URL", creativeUrlOf(ad.creative)),
      ...line("Tags", ad.tags.join(", ")),
    ]),
    ...section("Creative copy", copyLines(ad.creativeCopy)),
    ...section("Fatigue", fatigueLines(ad.fatigue)),
    ...section(
      `Performance (${dateRangeText(dateRange)})`,
      ad.metrics.flatMap((metric) => line(metric.label, metric.value)),
    ),
  ];

  return `<ad-context ${attrs}>\n${sections.join("\n\n")}\n</ad-context>`;
}

function fatigueAttrs(fatigue: AdForPrompt["fatigue"]): readonly string[] {
  return fatigue === "no-fatigue-row"
    ? []
    : [`fatigue-status="${escapeContextAttr(fatigue.status)}"`];
}

function statusLines(status: EntityStatus): readonly string[] {
  switch (status.kind) {
    case "status": {
      return line("Delivery status", status.value);
    }
    case "no-status": {
      return [];
    }
    default: {
      return status satisfies never;
    }
  }
}

function formatLines(media: PromptCreative["media"]): readonly string[] {
  return media.kind === "no-media"
    ? []
    : line("Format", media.kind.toUpperCase());
}

function creativeUrlOf(creative: PromptCreative): Found<string> {
  const { media } = creative;
  switch (media.kind) {
    case "no-media": {
      return { kind: "not-found" };
    }
    case "image": {
      return { kind: "found", value: media.url };
    }
    case "video": {
      switch (media.video.kind) {
        case "file": {
          return { kind: "found", value: media.video.url };
        }
        case "youtube": {
          return { kind: "found", value: media.video.thumbnailUrl };
        }
        default: {
          return media.video satisfies never;
        }
      }
    }
    default: {
      return media satisfies never;
    }
  }
}

function sizeLines(creative: PromptCreative): readonly string[] {
  const size = fileSizeOf(creative.media);
  return size === "no-file" ? [] : loadedSizeLines(size);
}

function loadedSizeLines(size: LoadedSize): readonly string[] {
  switch (size.kind) {
    case "measured": {
      return line("Dimensions", `${size.width}×${size.height}`);
    }
    case "load-failed": {
      return [];
    }
    default: {
      return size satisfies never;
    }
  }
}

function copyLines(copy: CreativeCopy): readonly string[] {
  return [
    ...foundLine("Primary text", copy.primaryText),
    ...foundLine("Headline", copy.headline),
    ...foundLine("Description", copy.description),
    ...foundLine("Call to action", copy.callToAction),
    ...foundLine("Destination", copy.destinationUrl),
  ];
}

function fatigueLines(fatigue: AdForPrompt["fatigue"]): readonly string[] {
  if (fatigue === "no-fatigue-row") {
    return [];
  }
  return [
    ...line("Status", fatigue.statusLabel),
    ...reportedLine("CTR vs peak", fatigue.vsPeakPct, 0),
    ...foundLine("Peak CTR", fatigue.peakCtr),
    ...reportedLine("Week-over-week CTR change", fatigue.wowCtrChange, 1),
    ...line("Days active", String(fatigue.daysActive)),
    ...foundLine("Likely cause", fatigue.cause),
  ];
}

function reportedLine(
  label: string,
  value: Reported,
  digits: number,
): readonly string[] {
  return value === "not-reported"
    ? []
    : line(label, `${value.toFixed(digits)}%`);
}

function foundLine(label: string, found: Found<string>): readonly string[] {
  switch (found.kind) {
    case "found": {
      return line(label, found.value);
    }
    case "not-found": {
      return [];
    }
    default: {
      return found satisfies never;
    }
  }
}

/** Every body value funnels through here, so it's the one place the
 * context-block escaping has to be applied. */
function line(label: string, value: string): readonly string[] {
  return value === "" ? [] : [`${label}: ${escapeContextText(value)}`];
}

function section(heading: string, lines: readonly string[]): readonly string[] {
  return lines.length > 0 ? [`## ${heading}\n${lines.join("\n")}`] : [];
}

function isPresent(value: string | null): value is string {
  return value !== null;
}
